import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface UserProfile {
  id: string;
  user_id: string;
  username: string | null;
  display_name: string | null;
  first_name: string | null;
  full_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  is_public: boolean | null;
  location: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// Explicit list: user_profiles SELECT is granted per column, so select('*') fails.
const USER_PROFILE_COLUMNS =
  'id, user_id, username, display_name, first_name, full_name, bio, avatar_url, is_public, location, created_at, updated_at';

interface UseUserProfileOptions {
  enabled?: boolean;
}

export function useUserProfile({ enabled = true }: UseUserProfileOptions = {}) {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  // useUserProfile() is mounted concurrently in multiple places (e.g. NavBar's
  // AuthPopover and the profile page) for the same user, so the channel name
  // must be unique per hook instance, not just per user, or the second
  // subscribe() collides with the first on the same realtime topic.
  const instanceIdRef = useRef(crypto.randomUUID());

  useEffect(() => {
    if (!enabled || !user?.id) {
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('user_profiles')
          .select(USER_PROFILE_COLUMNS)
          .eq('user_id', user.id)
          .maybeSingle();

        if (fetchError) {
          console.error('Error fetching user profile:', fetchError);
          setError(fetchError.message);
          setProfile(null);
        } else {
          setProfile(data);
        }
      } catch (err) {
        console.error('Unexpected error fetching profile:', err);
        setError('Failed to load profile');
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();

    // Subscribe to profile changes
    const subscription = supabase
      .channel(`profile-changes-${user.id}-${instanceIdRef.current}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('Profile updated:', payload);
          if (payload.new) {
            setProfile(payload.new as UserProfile);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [enabled, user?.id]);

  const refreshProfile = async () => {
    if (!enabled || !user?.id) return;

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(USER_PROFILE_COLUMNS)
        .eq('user_id', user.id)
        .maybeSingle();

      if (!error && data) {
        setProfile(data);
      }
    } catch (err) {
      console.error('Error refreshing profile:', err);
    }
  };

  const scopedProfile = enabled && user?.id ? profile : null;
  const scopedLoading = enabled && user?.id ? loading : false;
  const scopedError = enabled && user?.id ? error : null;

  // Helper to get the display name with fallbacks
  const getDisplayName = () => {
    if (scopedProfile?.display_name) return scopedProfile.display_name;
    if (scopedProfile?.full_name) return scopedProfile.full_name;
    if (scopedProfile?.first_name) return scopedProfile.first_name;
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  // Helper to get avatar URL with fallback
  const getAvatarUrl = () => {
    return scopedProfile?.avatar_url ||
           user?.user_metadata?.avatar_url || 
           'https://vhaizqhkodqyqplpqcss.supabase.co/storage/v1/object/public/avatars/default-avatar.png';
  };

  return {
    profile: scopedProfile,
    loading: scopedLoading,
    error: scopedError,
    refreshProfile,
    displayName: getDisplayName(),
    avatarUrl: getAvatarUrl(),
    username: scopedProfile?.username || null,
    bio: scopedProfile?.bio || null,
    isPublic: scopedProfile?.is_public ?? true,
  };
}
